import { WorkflowType } from '@communities/content-management/domain';
import { LabelSelector, type ScoreLabelOption, type Label, type ScoreConfig, getScoreLabel, getScoreDescription } from '@core/components/LabelSelector';
import { ContentType, type StandardLabel } from '@system-config/label-management/domain';
import { useMemo, useCallback } from 'react';

import { PRESENCE_CONFIG } from '../../constants';
import type { DialogImageLabels } from '../../types';
import { useDialogContext } from '../DialogProvider';

import { stepFormStyles, inputGroupStyles } from './Steps.css';

/**
 * ImageLabelsStep component props
 */
export interface ImageLabelsStepProps {
  standardLabels: StandardLabel[];
  labelsLoading: boolean;
}

/**
 * ImageLabelsStep component - Step 3 of the dialog
 * For classification: score threshold-based image labels
 * For moderation: presence-based image labels (uses same LabelSelector grid but without score sliders)
 */
export function ImageLabelsStep({ standardLabels, labelsLoading }: ImageLabelsStepProps) {
  const { state, actions, workflowType } = useDialogContext();
  const { imageLabels } = state.formData;

  // Filter standard labels for image content
  const imageLabelOptions: ScoreLabelOption[] = useMemo(
    () =>
      standardLabels
        .filter(label => label.contentType === ContentType.Image)
        .map(label => ({
          id: label.id,
          labelName: label.labelName,
        })),
    [standardLabels]
  );

  // For classification: score-based configuration
  const scoreConfig: ScoreConfig = useMemo(
    () => ({
      mode: 'score' as const,
      min: 0,
      max: 100,
      defaultValue: 70,
      showDescription: true,
      getValueLabel: getScoreLabel,
      getValueDescription: getScoreDescription,
    }),
    []
  );

  // Classification handler
  const handleImageLabelsChange = useCallback(
    (labels: Label[]) => {
      const updatedImageLabels: DialogImageLabels = {
        imageLabels: labels,
      };

      actions.updateFormData({
        imageLabels: updatedImageLabels,
      });
    },
    [actions]
  );

  // Moderation: presence-based - reuse LabelSelector but store with scoreThreshold=0
  // The LabelSelector will show the grid; we override the onChange to use presence semantics
  const handleModerationImageLabelsChange = useCallback(
    (labels: Label[]) => {
      // For moderation, we just use presence — no actual score value matters
      actions.updateFormData({
        imageLabels: { imageLabels: labels },
      });
    },
    [actions]
  );

  if (labelsLoading) {
    return (
      <div className={stepFormStyles}>
        <div aria-live="polite" className={inputGroupStyles} role="status">
          <p>Loading image labels...</p>
        </div>
      </div>
    );
  }

  if (imageLabelOptions.length === 0) {
    return (
      <div className={stepFormStyles}>
        <div aria-live="polite" className={inputGroupStyles} role="status">
          <p>No image labels available. Please configure labels in the system settings.</p>
        </div>
      </div>
    );
  }

  // Moderation workflow: presence-based image labels (reuse LabelSelector grid, hide sliders)
  if (workflowType === WorkflowType.MODERATION) {
    return (
      <div className={stepFormStyles}>
        <div className={inputGroupStyles}>
          <LabelSelector
            enableBulkSelection
            availableLabels={imageLabelOptions}
            config={PRESENCE_CONFIG}
            emptyStateMessage="No image labels selected. Add labels to define image content criteria."
            loading={labelsLoading}
            selectedLabels={imageLabels.imageLabels}
            title="Image Content Labels"
            onChange={handleModerationImageLabelsChange}
          />
        </div>
      </div>
    );
  }

  // Classification workflow: score-based image labels (with slider)
  return (
    <div className={stepFormStyles}>
      <div className={inputGroupStyles}>
        <LabelSelector
          enableBulkSelection
          availableLabels={imageLabelOptions}
          config={scoreConfig}
          emptyStateMessage="No image labels selected. Add labels to define image content criteria."
          loading={labelsLoading}
          selectedLabels={imageLabels.imageLabels}
          title="Image Content Labels"
          onChange={handleImageLabelsChange}
        />
      </div>
    </div>
  );
}
